/**
 * L'ONGLET FACTURES, CÔTÉ ÉCRAN — lot 106, 18/09/2026.
 *
 * Ce fichier ne dessine rien. Il convertit ce que l'API rend, calcule la date
 * de paiement PRÉVUE avec la fonction de l'échéancier (jamais une seconde
 * formule), filtre, trie, pagine, et fabrique l'export.
 *
 * POURQUOI TOUT SE PASSE ICI ET NON SUR LE SERVEUR : l'API rend les factures
 * une fois, l'écran fait le reste. Filtrer devient immédiat, et surtout
 * l'export porte sur le FILTRE COURANT et non sur la page affichée — c'est ce
 * qu'on attend d'un écran qui sert à vérifier des chiffres.
 */

import {
  centimes,
  partsDe,
  sortDeLaFacture,
  totauxDe,
  LIBELLES_MOTIF,
  type FactureLue,
  type MotifCompte,
  type PartDatee,
  type SensFacture,
  type TotauxFactures,
} from '@stafa/core';
import { dateFr, euros } from './echeancier.js';

/** Une facture telle que `GET /analyse/factures` la rend. */
export interface FactureApi {
  readonly sens: SensFacture;
  readonly pennylane_id: string;
  readonly numero: string | null;
  readonly libelle: string | null;
  readonly tiers: { readonly cle: string; readonly nom: string };
  readonly activite_id: string | null;
  readonly date_piece: string | null;
  readonly echeance: string | null;
  readonly montant_ht: string | null;
  readonly tva: string | null;
  readonly montant_ttc: string;
  readonly reste_du: string;
  readonly payee: boolean;
  readonly statut: string | null;
  readonly regle_le: string | null;
  readonly regle_cherche: boolean;
  readonly mode_paiement: string | null;
  readonly prelevement: boolean;
  readonly cedee_le: string | null;
  readonly facture_id: string | null;
  readonly retard: { readonly jours: number | null };
  readonly paiements: readonly {
    readonly montant: string | null;
    readonly date: string;
  }[];
  /** Qui a fixé la date à la main, et quand. Nul : calcul automatique. */
  readonly saisie: { readonly par: string; readonly le: string } | null;
  readonly ecartee: {
    readonly motif: string;
    readonly par: string;
    readonly le: string;
  } | null;
}

export interface FacturesLues {
  readonly aujourdhui: string;
  readonly factures: readonly FactureApi[];
}

/**
 * LA DATE DE PAIEMENT PRÉVUE, par la fonction de l'échéancier.
 *
 * Une pièce soldée n'en a pas : elle a une date RÉELLE, et proposer une
 * prévision à côté ferait douter de laquelle croire. Une pièce sans reste non
 * plus — `partsDe` rend alors une liste vide, et c'est le bon comportement.
 */
export function prevueDe(f: FactureApi, aujourdhui: string): string | null {
  // UNE PIÈCE ÉCARTÉE N'A PLUS DE DATE PRÉVUE, et c'est un défaut corrigé au
  // lot 107 : la liste en affichait une alors que l'échéancier ne la connaît
  // plus. Une date qui ne mène nulle part est pire qu'une date absente.
  if (f.ecartee !== null) return null;
  if (f.payee) return null;
  const reste = centimes(f.reste_du);
  if (reste <= 0) return null;
  const parts = partsDe(
    {
      echeance: f.echeance,
      date_piece: f.date_piece,
      reste_centimes: reste,
      prelevement: f.prelevement,
      retard_jours: f.retard.jours,
      tranches:
        f.paiements.length === 0
          ? null
          : f.paiements.map((p) => ({
              montant_centimes: p.montant === null ? null : centimes(p.montant),
              date: p.date,
            })),
    },
    aujourdhui,
  );
  return parts[0]?.date ?? null;
}

/** Ce que l'écran manipule : la facture de l'API, convertie et complétée. */
export interface Ligne extends FactureLue {
  readonly activite_id: string | null;
  readonly mode_paiement: string | null;
  readonly facture_id: string | null;
  /** Qui a fixé la date de paiement à la main, et quand. */
  readonly saisie: { readonly par: string; readonly le: string } | null;
  readonly paiements: readonly {
    readonly montant: string | null;
    readonly date: string;
  }[];
}

export function lignesDe(lu: FacturesLues): Ligne[] {
  return lu.factures.map((f) => ({
    sens: f.sens,
    pennylane_id: f.pennylane_id,
    numero: f.numero,
    libelle: f.libelle,
    tiers_nom: f.tiers.nom,
    date_piece: f.date_piece,
    echeance: f.echeance,
    ht: f.montant_ht === null ? null : centimes(f.montant_ht),
    tva: f.tva === null ? null : centimes(f.tva),
    ttc: centimes(f.montant_ttc),
    reste: f.payee ? 0 : centimes(f.reste_du),
    payee: f.payee,
    statut: f.statut,
    regle_le: f.regle_le,
    regle_cherche: f.regle_cherche,
    cedee_le: f.cedee_le,
    prevue_le: prevueDe(f, lu.aujourdhui),
    ecartee: f.ecartee,
    activite_id: f.activite_id,
    mode_paiement: f.mode_paiement,
    facture_id: f.facture_id,
    saisie: f.saisie,
    paiements: f.paiements,
  }));
}

// ---------------------------------------------------------------------------
// Les filtres
// ---------------------------------------------------------------------------

export type QuelSens = 'tout' | SensFacture;
export type QuelStatut = 'tout' | 'ouverte' | 'partielle' | 'payee';
export type QuelCompte = 'tout' | 'comptee' | 'non_comptee' | 'ecartee';

/**
 * QUI A DÉCIDÉ DU SORT DE CETTE PIÈCE — demandé le 18/09/2026, en remplacement
 * de la colonne « compté au BFR » dans la liste : l'information est dans la
 * fenêtre, une colonne de plus ne servait qu'à charger l'écran.
 *
 * « à la main » veut dire que QUELQU'UN EST PASSÉ : il a fixé la date de
 * paiement, ou écarté la pièce des calculs. « automatique » : personne n'y a
 * touché, tout vient du calcul.
 *
 * LA CESSION AU FACTOR N'EN FAIT PAS PARTIE, et c'est un choix : elle se
 * coche dans l'échéancier mais ne change pas le TRAITEMENT de la pièce, elle
 * décrit un fait — la facture a été cédée. À dire si vous voulez l'y mettre.
 */
export type QuelTraitement = 'tout' | 'main' | 'auto' | 'a_dater';
export type SurQuelleDate = 'piece' | 'echeance';

export interface FiltresFactures {
  readonly texte: string;
  readonly sens: QuelSens;
  readonly statut: QuelStatut;
  readonly compte: QuelCompte;
  readonly traitement: QuelTraitement;
  readonly sur: SurQuelleDate;
  readonly du: string;
  readonly au: string;
  /** Montant minimum en euros, tel qu'il est saisi. */
  readonly minimum: string;
  readonly echues: boolean;
}

export const SANS_FILTRE_FACTURES: FiltresFactures = {
  texte: '',
  sens: 'tout',
  statut: 'tout',
  compte: 'tout',
  traitement: 'tout',
  sur: 'piece',
  du: '',
  au: '',
  minimum: '',
  echues: false,
};

function sansAccents(t: string): string {
  return t.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

/**
 * QUELQU'UN EST-IL PASSÉ SUR CETTE PIÈCE ?
 *
 * Deux gestes le prouvent, et eux seuls : une date de paiement saisie
 * (`paiements`, écrits par l'échéancier) et une exclusion posée. Tout le reste
 * vient du calcul.
 */
export function aLaMain(l: Ligne): boolean {
  return l.ecartee !== null || l.paiements.length > 0;
}

/**
 * CE QUE LA COLONNE « TRAITEMENT » AFFICHE — lot 107.
 *
 * Trois valeurs et pas deux : « écartée » et « date fixée » sont deux gestes
 * humains différents, et les confondre sous un même mot ferait perdre
 * l'essentiel. « automatique » veut dire que personne n'y a touché.
 */
export type Traitement = 'automatique' | 'a_dater' | 'date_fixee' | 'ecartee';

/**
 * QUATRE VALEURS DEPUIS LE LOT 110, et la quatrième est la file de travail.
 *
 * « À DATER » : la pièce est ouverte, personne n'a fixé sa date, et le calcul
 * n'en propose aucune — parce qu'elle est échue et qu'on n'invente plus de
 * date (`partsDe`, echeancier.ts). Elle ne figure donc dans aucune semaine de
 * l'échéancier ni du plan de trésorerie, et attend une décision.
 *
 * LA CONDITION N'EST PAS RECOPIÉE ICI, et c'est délibéré : `prevue_le` est
 * déjà le résultat de `partsDe` (voir `prevueDe`). Redériver « est-elle
 * échue ? » dans ce fichier aurait fini par diverger du calcul — la colonne
 * aurait alors annoncé « à dater » des pièces que l'échéancier prévoit, ou
 * l'inverse. On lit donc le verdict au lieu de le refaire.
 *
 * `prevue_le` est également nul pour une pièce PAYÉE ou sans reste : d'où les
 * deux gardes, sans lesquelles des factures soldées seraient annoncées comme
 * à traiter.
 */
export function traitementDe(l: Ligne): Traitement {
  if (l.ecartee !== null) return 'ecartee';
  if (l.paiements.length > 0) return 'date_fixee';
  return l.prevue_le === null && !l.payee && l.reste > 0 ? 'a_dater' : 'automatique';
}

export const LIBELLES_TRAITEMENT: Readonly<Record<Traitement, string>> = {
  automatique: 'automatique',
  a_dater: 'à dater',
  date_fixee: 'date fixée à la main',
  ecartee: 'écartée à la main',
};

/** Le statut d'une pièce, tel que Stafa le juge — pas celui de Pennylane. */
export function statutDe(l: Ligne): QuelStatut {
  if (l.payee) return 'payee';
  return l.reste > 0 && l.reste < l.ttc ? 'partielle' : 'ouverte';
}

export function filtrerFactures(
  lignes: readonly Ligne[],
  f: FiltresFactures,
  aujourdhui: string,
): Ligne[] {
  const cherche = sansAccents(f.texte.trim());
  const minimum = f.minimum.trim() === '' ? null : Number(f.minimum.replace(',', '.'));
  return lignes.filter((l) => {
    if (f.sens !== 'tout' && l.sens !== f.sens) return false;
    if (f.statut !== 'tout' && statutDe(l) !== f.statut) return false;
    // LE FILTRE LIT LA MÊME COLONNE QUE LA LISTE (lot 110). Il s'appuyait sur
    // `aLaMain`, qui ne connaît que deux états : une pièce « à dater » serait
    // alors sortie sous « automatique », et la colonne aurait démenti le
    // filtre sur la même ligne.
    if (f.traitement !== 'tout') {
      const t = traitementDe(l);
      if (f.traitement === 'main' && t !== 'date_fixee' && t !== 'ecartee')
        return false;
      if (f.traitement === 'auto' && t !== 'automatique') return false;
      if (f.traitement === 'a_dater' && t !== 'a_dater') return false;
    }
    if (f.compte !== 'tout') {
      const s = sortDeLaFacture(l, aujourdhui);
      if (f.compte === 'comptee' && !s.bfr) return false;
      if (f.compte === 'non_comptee' && s.bfr) return false;
      if (f.compte === 'ecartee' && l.ecartee === null) return false;
    }
    if (cherche !== '') {
      const botte = sansAccents(
        [l.numero ?? '', l.tiers_nom, l.libelle ?? '', l.pennylane_id].join(' '),
      );
      if (!botte.includes(cherche)) return false;
    }
    const date = f.sur === 'piece' ? l.date_piece : l.echeance;
    // Une pièce sans la date sur laquelle on filtre sort dès qu'une borne est
    // posée : elle n'est ni avant ni après, et la garder ferait douter du total.
    if (f.du !== '' && (date === null || date < f.du)) return false;
    if (f.au !== '' && (date === null || date > f.au)) return false;
    if (minimum !== null && Number.isFinite(minimum) && l.ttc < minimum * 100) {
      return false;
    }
    if (f.echues) {
      if (l.echeance === null || l.echeance >= aujourdhui) return false;
      if (l.payee) return false;
    }
    return true;
  });
}

// ---------------------------------------------------------------------------
// Le tri
// ---------------------------------------------------------------------------

export type CleTriFactures =
  | 'tiers'
  | 'numero'
  | 'date_piece'
  | 'echeance'
  | 'ht'
  | 'tva'
  | 'ttc'
  | 'reste'
  | 'statut'
  | 'prevue'
  | 'reglee'
  | 'traitement';

export type Sens2 = 'haut' | 'bas';

function valeurTri(l: Ligne, cle: CleTriFactures): string | number {
  switch (cle) {
    case 'tiers':
      return sansAccents(l.tiers_nom);
    case 'numero':
      return sansAccents(l.numero ?? '');
    case 'date_piece':
      return l.date_piece ?? '';
    case 'echeance':
      return l.echeance ?? '';
    case 'ht':
      return l.ht ?? -1;
    case 'tva':
      return l.tva ?? -1;
    case 'ttc':
      return l.ttc;
    case 'reste':
      return l.reste;
    case 'statut':
      return statutDe(l);
    case 'prevue':
      return l.prevue_le ?? '';
    case 'traitement':
      // « automatique » d'abord, puis les deux gestes humains : trier sur
      // cette colonne doit faire remonter ce sur quoi on est passé.
      return traitementDe(l);
    default:
      return l.regle_le ?? '';
  }
}

export function trierFactures(
  lignes: readonly Ligne[],
  cle: CleTriFactures,
  sens: Sens2,
): Ligne[] {
  const signe = sens === 'haut' ? 1 : -1;
  return [...lignes].sort((a, b) => {
    const x = valeurTri(a, cle);
    const y = valeurTri(b, cle);
    if (x < y) return -signe;
    if (x > y) return signe;
    // À valeur égale, un ordre stable : sinon deux lectures de suite ne
    // donnent pas la même liste, et on croit à un changement de données.
    return a.pennylane_id < b.pennylane_id ? -1 : 1;
  });
}

// ---------------------------------------------------------------------------
// La pagination
// ---------------------------------------------------------------------------

/** 0 veut dire « tout », demandé le 18/09. */
export const TAILLES_PAGE: readonly number[] = [20, 50, 100, 200, 0];

export function pageDe(
  lignes: readonly Ligne[],
  taille: number,
  page: number,
): { readonly lignes: Ligne[]; readonly pages: number; readonly page: number } {
  if (taille <= 0) return { lignes: [...lignes], pages: 1, page: 1 };
  const pages = Math.max(1, Math.ceil(lignes.length / taille));
  const courante = Math.min(Math.max(1, page), pages);
  const debut = (courante - 1) * taille;
  return { lignes: lignes.slice(debut, debut + taille), pages, page: courante };
}

// ---------------------------------------------------------------------------
// Les totaux et l'export
// ---------------------------------------------------------------------------

export function totauxFiltres(
  lignes: readonly Ligne[],
  aujourdhui: string,
): TotauxFactures {
  return totauxDe(lignes, aujourdhui);
}

export const LIBELLES_STATUT: Readonly<Record<QuelStatut, string>> = {
  tout: 'tous',
  ouverte: 'ouverte',
  partielle: 'partiellement réglée',
  payee: 'payée',
};

/** Ce que dit la colonne « compté au BFR », motif compris. */
export function compteEnMots(l: Ligne, aujourdhui: string): string {
  const s = sortDeLaFacture(l, aujourdhui);
  if (s.bfr) return s.cedee ? 'comptée, cédée au factor' : 'comptée';
  return LIBELLES_MOTIF[s.motif];
}

export function motifDe(l: Ligne, aujourdhui: string): MotifCompte {
  return sortDeLaFacture(l, aujourdhui).motif;
}

/**
 * L'EXPORT, en CSV séparé par des points-virgules et en nombres à virgule :
 * c'est ce qu'Excel en français ouvre sans rien demander. Un CSV « correct »
 * à la virgule anglaise s'ouvrirait en une seule colonne, et l'outil censé
 * servir à vérifier ferait perdre une demi-heure.
 *
 * L'export porte sur LE FILTRE COURANT, toutes pages confondues.
 */
export function exportCsv(lignes: readonly Ligne[], aujourdhui: string): string {
  const colonnes = [
    'Sens',
    'Tiers',
    'N° pièce',
    'Libellé',
    'Date pièce',
    'Échéance',
    'HT',
    'TVA',
    'TTC',
    'Reste dû',
    'Statut',
    'Paiement prévu',
    'Paiement réel',
    'Compté au BFR',
    'Motif exclusion',
    'Identifiant Pennylane',
  ];
  const nombre = (c: number | null) =>
    c === null ? '' : (c / 100).toFixed(2).replace('.', ',');
  const champ = (v: string) => (/[";\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const lignesCsv = lignes.map((l) =>
    [
      l.sens === 'client' ? 'Client' : 'Fournisseur',
      l.tiers_nom,
      l.numero ?? '',
      l.libelle ?? '',
      l.date_piece ?? '',
      l.echeance ?? '',
      nombre(l.ht),
      nombre(l.tva),
      nombre(l.ttc),
      nombre(l.reste),
      LIBELLES_STATUT[statutDe(l)],
      l.prevue_le ?? '',
      l.regle_le ?? '',
      compteEnMots(l, aujourdhui),
      l.ecartee?.motif ?? '',
      l.pennylane_id,
    ]
      .map(champ)
      .join(';'),
  );
  // Le BOM : sans lui, Excel lit les accents de travers.
  return `\uFEFF${colonnes.map(champ).join(';')}\r\n${lignesCsv.join('\r\n')}\r\n`;
}

export function nomDuFichier(aujourdhui: string): string {
  return `factures-${aujourdhui}.csv`;
}

export { dateFr, euros };

/**
 * LES PARTS D'UNE PIÈCE, telles que l'échéancier les calcule — lot 107.
 *
 * La fenêtre de détail en a besoin pour dire « où et quand » la pièce est
 * prise en compte. On repasse par `partsDe`, la fonction de l'échéancier,
 * plutôt que de relire `prevue_le` : `prevue_le` n'est que la PREMIÈRE part,
 * et une pièce découpée en trois paiements en a trois.
 */
export function partsPourLEcheancier(l: Ligne, aujourdhui: string): PartDatee[] {
  if (l.payee || l.reste <= 0 || l.ecartee !== null) return [];
  return partsDe(
    {
      echeance: l.echeance,
      date_piece: l.date_piece,
      reste_centimes: l.reste,
      prelevement: false,
      retard_jours: null,
      tranches:
        l.prevue_le === null ? null : [{ montant_centimes: null, date: l.prevue_le }],
    },
    aujourdhui,
  ).map((p) => ({
    date: p.date,
    montant_centimes: p.montant_centimes,
    a_revoir: p.a_revoir,
  }));
}
